import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import moment from 'moment';
import { useDecodedParam } from '@app/hooks/useDecodedParam';
import {
  getStudentAutoMarkingById,
  getEvaluationPreview,
  StudentAutoMarkingDetail,
  StudentAutoMarkingQuestion,
} from '@app/services/studentAutoMarking';

import './StudentAutoMarking.css';

// ─── Helpers ──────────────────────────────────────────────────────────────────

// ─── Question Card — compact ──────────────────────────────────────────────────

interface QuestionCardProps {
  question: StudentAutoMarkingQuestion;
  index: number;
}

const statusMeta = (status: string) => {
  if (status === 'correct')    return { bg: '#f0fdf4', border: '#86efac', text: '#166534', icon: <i className="fas fa-check-circle" style={{ color: '#22c55e' }} /> };
  if (status === 'partial')    return { bg: '#fffbeb', border: '#fcd34d', text: '#92400e', icon: <i className="fas fa-exclamation-circle" style={{ color: '#f59e0b' }} /> };
  if (status === 'incorrect')  return { bg: '#fff1f2', border: '#fca5a5', text: '#991b1b', icon: <i className="fas fa-times-circle" style={{ color: '#ef4444' }} /> };
  return                              { bg: '#f8fafc', border: '#cbd5e1', text: '#475569', icon: <i className="fas fa-minus-circle" style={{ color: '#94a3b8' }} /> };
};

const QuestionCard = ({ question }: QuestionCardProps) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const s = statusMeta(question.status);

  return (
    <div style={{
      marginBottom: 6, borderRadius: 8,
      border: `1px solid ${s.border}`,
      overflow: 'hidden', fontSize: '0.82rem',
    }}>
      {/* ── Header row ── */}
      <div style={{
        background: s.bg, padding: '5px 12px',
        display: 'flex', alignItems: 'center', gap: 8,
        borderBottom: `1px solid ${s.border}`,
      }}>
        {s.icon}
        <span style={{ fontWeight: 700, color: s.text, whiteSpace: 'nowrap' }}>
          Q{question.question_number}
        </span>
        <span style={{ flex: 1, color: '#1e293b', fontWeight: 500 }}>
          {question.question_text}
        </span>
        <span style={{
          background: s.border, color: s.text,
          borderRadius: 20, padding: '1px 8px',
          fontWeight: 700, whiteSpace: 'nowrap', fontSize: '0.75rem',
          border: `1px solid ${s.border}`,
        }}>
          {question.marks_awarded}/{question.max_marks}
        </span>
      </div>

      {/* ── Answers row ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', background: '#fff' }}>
        <div style={{ padding: '5px 12px', borderRight: '1px solid #f1f5f9' }}>
          <div style={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Student</div>
          <div style={{
            color: question.status === 'correct' ? '#16a34a' : question.status === 'partial' ? '#d97706' : '#ef4444',
            fontWeight: 500, marginTop: 1,
          }}>
            {question.student_answer || <em style={{ color: '#cbd5e1' }}>No answer</em>}
          </div>
        </div>
        <div style={{ padding: '5px 12px' }}>
          <div style={{ color: '#94a3b8', fontSize: '0.68rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>Correct</div>
          <div style={{ color: '#2563eb', fontWeight: 500, marginTop: 1 }}>{question.correct_answer}</div>
        </div>
      </div>

      {/* ── Feedback toggle (non-correct only) ── */}
      {question.feedback && question.status !== 'correct' && (
        <div style={{ background: '#fffbeb', borderTop: `1px solid ${s.border}`, padding: '4px 12px' }}>
          <button
            type="button"
            onClick={() => setShowFeedback(!showFeedback)}
            style={{ background: 'none', border: 'none', padding: 0, color: '#b45309', fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer' }}
          >
            <i className={`fas fa-comment-alt mr-1`} />
            {showFeedback ? 'Hide' : 'Show'} Feedback
          </button>
          {showFeedback && (
            <div style={{ color: '#92400e', fontSize: '0.78rem', marginTop: 4, paddingBottom: 4 }}>
              {question.feedback}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Zoomable Image ───────────────────────────────────────────────────────────

const ZoomableImage = ({ src, alt }: { src: string; alt: string }) => {
  const [zoom, setZoom] = useState(100);
  return (
    <div>
      {/* Zoom controls */}
      <div className="d-flex justify-content-center align-items-center mb-2" style={{ gap: 8 }}>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setZoom(z => Math.max(z - 25, 25))}
          disabled={zoom <= 25}
        >
          <i className="fas fa-search-minus" />
        </button>
        <span style={{ minWidth: 48, textAlign: 'center', fontSize: '0.85rem', color: '#6b7280' }}>{zoom}%</span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setZoom(z => Math.min(z + 25, 300))}
          disabled={zoom >= 300}
        >
          <i className="fas fa-search-plus" />
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          onClick={() => setZoom(100)}
        >
          <i className="fas fa-expand-arrows-alt" /> Reset
        </button>
      </div>
      {/* Scrollable image container */}
      <div style={{ overflow: 'visible', background: '#f8f9fa', borderRadius: 6, padding: 4 }}>
        <img
          src={src}
          alt={alt}
          style={{
            width: `${zoom}%`,
            display: 'block',
            margin: '0 auto',
            borderRadius: 4,
            boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
            transition: 'width 0.2s ease',
          }}
        />
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

const getPerformanceLabel = (pct: number): { label: string; color: string } => {
  if (pct >= 90) return { label: 'Excellent',         color: '#10b981' };
  if (pct >= 75) return { label: 'Good',              color: '#3b82f6' };
  if (pct >= 60) return { label: 'Satisfactory',      color: '#f59e0b' };
  if (pct >= 40) return { label: 'Needs Improvement', color: '#f97316' };
  return               { label: 'Poor',               color: '#ef4444' };
};

const ViewStudentAutoMarking = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const params   = useParams<{ studentId?: string; encodedId?: string }>();
  // studentId comes from new URL /student/:studentId/auto-marking/:encodedId
  // or from navigation state when coming from old URL
  const studentEncodedId = params.studentId ?? (location.state as any)?.studentEncodedId ?? null;
  // Guard in App.tsx validates both studentId and encodedId before this mounts
  const id = useDecodedParam('encodedId', '/students');
  const isMounted = useRef(true);

  const [detail, setDetail] = useState<StudentAutoMarkingDetail | null>(null);
  const [isLoading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'results' | 'preview'>('results');

  // Preview modal state — single API call, both URLs stored
  const [previewModal, setPreviewModal] = useState<{
    open: boolean;
    loading: boolean;
    activeView: 'worksheet' | 'handbook';
    worksheetUrl: string | null;
    handbookUrl: string | null;
    handbookType: 'png' | 'pdf' | null;
  }>({ open: false, loading: false, activeView: 'worksheet', worksheetUrl: null, handbookUrl: null, handbookType: null });

  useEffect(() => {
    return () => { isMounted.current = false; };
  }, []);

  useEffect(() => {
    if (id) fetchDetail(id);
  }, [id]);

  const fetchDetail = async (recordId: number) => {
    try {
      setLoading(true);
      const resp = await getStudentAutoMarkingById(recordId);
      if (!isMounted.current) return;
      if (resp?.success) {
        setDetail(resp.data);
      } else {
        toast.error(resp?.errors?.[0]?.msg || 'Failed to load details');
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (err: any) {
      if (isMounted.current) toast.error(err?.message || 'Internal Server Error');
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const openPreview = async (startView: 'worksheet' | 'handbook') => {
    if (!detail?.evaluationId) return;
    setPreviewModal({ open: true, loading: true, activeView: startView, worksheetUrl: null, handbookUrl: null, handbookType: null });
    try {
      const resp = await getEvaluationPreview(detail.evaluationId);
      if (resp?.success && resp.data) {
        setPreviewModal({
          open: true, loading: false, activeView: startView,
          worksheetUrl: resp.data.uploadedWorksheetUrl || null,
          handbookUrl:  resp.data.handbookUrl || null,
          handbookType: resp.data.handbookType || 'png',
        });
      } else {
        setPreviewModal(prev => ({ ...prev, loading: false }));
        toast.error('Preview not available.');
      }
    } catch {
      setPreviewModal(prev => ({ ...prev, loading: false }));
      toast.error('Failed to load preview.');
    }
  };

  const closePreview = () =>
    setPreviewModal({ open: false, loading: false, activeView: 'worksheet', worksheetUrl: null, handbookUrl: null, handbookType: null });

  if (isLoading) {    return (
      <div style={{
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        minHeight: '60vh', gap: 12,
      }}>
        <i className="fas fa-spinner fa-spin fa-2x text-muted" />
        <p className="text-muted mb-0">Loading details...</p>
      </div>
    );
  }

  if (!detail) return null;

  const questions = detail.questionDetails || detail.questions || [];
  const summary = detail.summary ? {
    total_questions:   detail.summary.totalQuestions   ?? detail.summary.total_questions   ?? 0,
    correct_answers:   detail.summary.correctAnswers   ?? detail.summary.correct_answers   ?? 0,
    partially_correct: detail.summary.partiallyCorrect ?? detail.summary.partially_correct ?? 0,
    incorrect:         detail.summary.incorrect        ?? 0,
    unanswered:        detail.summary.unanswered       ?? 0,
    total_marks:       detail.summary.totalMarks       ?? detail.summary.total_marks       ?? 0,
    max_marks:         detail.summary.maxMarks         ?? detail.summary.max_marks         ?? 0,
    percentage:        detail.summary.percentage       ?? 0,
  } : null;
  const percentage = summary?.percentage ?? 0;

  return (
    <div>
      <section className="content-header">
        <div className="container-fluid">
          <div className="row mb-2">
            <div className="col-sm-6">
              <h1>
                Auto Marking
                {detail && (
                  <small className="ml-2 text-muted" style={{ fontSize: '0.9rem', fontWeight: 400 }}>
                    {detail.student
                      ? `— ${[detail.student.firstName, (detail.student as any)?.middleName, detail.student.lastName].filter(Boolean).join(' ')}`
                      : detail.student_name ? `— ${detail.student_name}` : ''}
                    {(detail.subjectName || detail.subject_name) && (
                      <span className="ml-1">
                        · {(detail.subjectName || detail.subject_name || '').replace(/\b\w/g, c => c.toUpperCase())}
                        {detail.module ? ` · ${detail.module}` : ''}
                      </span>
                    )}
                  </small>
                )}
              </h1>
            </div>
            <div className="col-sm-6">
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item"><a href="/">Home</a></li>
                <li className="breadcrumb-item"><Link to="/students">Students</Link></li>
                {studentEncodedId && (
                  <li className="breadcrumb-item">
                    <Link to={`/student/${studentEncodedId}/auto-marking`}>
                      Auto Marking
                    </Link>
                  </li>
                )}
                <li className="breadcrumb-item active">View</li>
              </ol>
            </div>
          </div>
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">

          {/* ── Header Card ── */}
          <div className="card mb-4">
            <div className="sam-detail-header">
              <div className="d-flex justify-content-between align-items-start flex-wrap">
                <div>
                  {/* Student name · Subject · Module on one line */}
                  <h4 style={{ marginBottom: 4 }}>
                    {detail.student
                      ? [detail.student.firstName, (detail.student as any)?.middleName, detail.student.lastName].filter(Boolean).join(' ')
                      : detail.student_name || ''}
                    <span style={{ opacity: 0.75, fontWeight: 400, marginLeft: 10 }}>
                      &middot;&ensp;
                      {(detail.subjectName || detail.subject_name || '').replace(/\b\w/g, c => c.toUpperCase())}
                      {(detail.module || detail.module_name) ? ` · ${detail.module || detail.module_name}` : ''}
                    </span>
                  </h4>
                  {/* WS · Handbook page · Date */}
                  <p className="mb-0" style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.85)', marginTop: 3 }}>
                    <i className="fas fa-file-alt mr-1" />
                    {detail.module || detail.module_name}
                    {detail.pageNumber ? ` — ${detail.pageNumber}` : ''}
                    &ensp;|&ensp;
                    <i className="fas fa-book mr-1" />
                    Handbook page {detail.handbookPage}
                    {(detail.createdAt || detail.created_at) && (
                      <>
                        &ensp;|&ensp;
                        <i className="fas fa-calendar mr-1" />
                        {moment(detail.createdAt || detail.created_at).format('DD MMM YYYY, hh:mm A')}
                      </>
                    )}
                  </p>
                </div>
                {summary && (
                  <div style={{
                    background: '#fff',
                    borderRadius: 20,
                    padding: '8px 18px',
                    textAlign: 'center',
                    lineHeight: 1.3,
                    minWidth: 140,
                  }}>
                    <div style={{ color: '#1e293b', fontWeight: 800, fontSize: '1rem', letterSpacing: 0.3 }}>
                      {summary.total_marks} / {summary.max_marks}
                      <span style={{ fontWeight: 500, fontSize: '0.82rem', marginLeft: 5, color: '#64748b' }}>({percentage}%)</span>
                    </div>
                    <div style={{
                      fontSize: '0.7rem', fontWeight: 800, marginTop: 3,
                      color: getPerformanceLabel(percentage).color,
                      letterSpacing: 0.8, textTransform: 'uppercase',
                    }}>
                      {getPerformanceLabel(percentage).label}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Stats row */}
            {summary && (
              <div className="card-body pb-3">
                {/* Stats row — inline grid, no Bootstrap row/col */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8, marginBottom: 14 }}>
                  {[
                    { label: 'Total',      value: summary.total_questions,   color: '#3b82f6', bg: 'linear-gradient(135deg,#eff6ff,#dbeafe)' },
                    { label: 'Correct',    value: summary.correct_answers,   color: '#10b981', bg: 'linear-gradient(135deg,#ecfdf5,#d1fae5)' },
                    { label: 'Partial',    value: summary.partially_correct, color: '#f59e0b', bg: 'linear-gradient(135deg,#fffbeb,#fef3c7)' },
                    { label: 'Incorrect',  value: summary.incorrect,         color: '#ef4444', bg: 'linear-gradient(135deg,#fff1f2,#ffe4e6)' },
                    { label: 'Unanswered', value: summary.unanswered,        color: '#6b7280', bg: 'linear-gradient(135deg,#f9fafb,#f3f4f6)' },
                  ].map(({ label, value, color, bg }) => (
                    <div key={label} style={{
                      background: bg, borderRadius: 10,
                      padding: '10px 8px', textAlign: 'center',
                      border: `1px solid ${color}20`,
                    }}>
                      <div style={{ color, fontWeight: 800, fontSize: '1.4rem', lineHeight: 1 }}>{value}</div>
                      <div style={{ color: '#6b7280', fontSize: '0.7rem', marginTop: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>{label}</div>
                    </div>
                  ))}
                </div>

                {/* Progress bar */}
                <div style={{ display: 'flex', height: 8, borderRadius: 999, overflow: 'hidden', background: '#f3f4f6', gap: 2 }}>
                  {summary.correct_answers > 0 && (
                    <div style={{ flex: summary.correct_answers, background: 'linear-gradient(90deg,#34d399,#10b981)', borderRadius: 999 }} />
                  )}
                  {summary.partially_correct > 0 && (
                    <div style={{ flex: summary.partially_correct, background: 'linear-gradient(90deg,#fbbf24,#f59e0b)', borderRadius: 999 }} />
                  )}
                  {summary.incorrect > 0 && (
                    <div style={{ flex: summary.incorrect, background: 'linear-gradient(90deg,#f87171,#ef4444)', borderRadius: 999 }} />
                  )}
                  {summary.unanswered > 0 && (
                    <div style={{ flex: summary.unanswered, background: '#cbd5e1', borderRadius: 999 }} />
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Tabs + Back button inline ── */}
          <ul className="nav nav-tabs mb-3" style={{ alignItems: 'center' }}>
            <li className="nav-item">
              <button
                type="button"
                className={`nav-link${activeTab === 'results' ? ' active' : ''}`}
                onClick={() => setActiveTab('results')}
              >
                <i className="fas fa-list-ol mr-2" />
                Questions &amp; Answers ({questions.length})
              </button>
            </li>
            {detail.teacherSummary && (
              <li className="nav-item">
                <button
                  type="button"
                  className={`nav-link${activeTab === 'preview' ? ' active' : ''}`}
                  onClick={() => setActiveTab('preview')}
                >
                  <i className="fas fa-chalkboard-teacher mr-2" />
                  Teacher Summary
                </button>
              </li>
            )}
            {/* Back button tucked to the right of the tab bar */}
            <li className="nav-item ml-auto d-flex align-items-center" style={{ gap: 6 }}>
              {detail?.evaluationId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => openPreview('worksheet')}
                  title="Preview uploaded worksheet"
                >
                  <i className="fas fa-file-image mr-1" /> Preview Worksheet
                </button>
              )}
              {detail?.evaluationId && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => openPreview('handbook')}
                  title="Preview handbook answer key"
                >
                  <i className="fas fa-book mr-1" /> Preview Handbook
                </button>
              )}
              <button
                type="button"
                className="btn btn-secondary btn-sm"
                onClick={() => navigate(-1)}
                style={{ marginBottom: 2 }}
              >
                <i className="fas fa-arrow-left mr-1" />
                Back
              </button>
            </li>
          </ul>

          {/* ── Questions Tab ── */}
          {activeTab === 'results' && (
            <div>
              {questions.length === 0 ? (
                <div className="card">
                  <div className="card-body text-center text-muted py-5">
                    <i className="fas fa-inbox fa-3x mb-3 d-block" />
                    No question data available yet.
                  </div>
                </div>
              ) : (
                questions.map((q, i) => {
                  // Normalize camelCase API fields → snake_case expected by QuestionCard
                  const normalized = {
                    ...q,
                    question_number: q.questionNumber ?? q.question_number,
                    question_text:   q.questionText   ?? q.question_text,
                    student_answer:  q.studentAnswer  ?? q.student_answer,
                    correct_answer:  q.correctAnswer  ?? q.correct_answer,
                    marks_awarded:   q.marksAwarded   ?? q.marks_awarded,
                    max_marks:       q.maxMarks        ?? q.max_marks,
                  };
                  return <QuestionCard key={i} question={normalized} index={i} />;
                })
              )}
            </div>
          )}

          {/* ── Teacher Summary Tab ── */}
          {activeTab === 'preview' && detail.teacherSummary && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">
                  <i className="fas fa-chalkboard-teacher mr-2" />
                  Teacher Summary
                </h3>
              </div>
              <div className="card-body">
                <div style={{
                  whiteSpace: 'pre-wrap',
                  lineHeight: 1.8,
                  fontSize: '0.9rem',
                  color: '#374151',
                }}>
                  {detail.teacherSummary
                    .replace(/\*\*(.*?)\*\*/g, '$1') // strip markdown bold
                    .replace(/^• /gm, '• ')}
                </div>
              </div>
            </div>
          )}

        </div>
      </section>

      {/* ── Preview Modal — dual A4 pane ── */}
      {previewModal.open && (
        <div
          className="modal fade show"
          style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.72)' }}
          onClick={closePreview}
        >
          <div
            className="modal-dialog"
            style={{ maxWidth: '92vw', margin: '20px auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              {/* Header */}
              <div className="modal-header py-2">
                <h5 className="modal-title" style={{ fontSize: '0.95rem' }}>
                  <i className="fas fa-eye mr-2" /> Preview Documents
                </h5>
                <button type="button" className="close" onClick={closePreview}>
                  <span>&times;</span>
                </button>
              </div>

              {/* Body — side-by-side A4 panes */}
              <div className="modal-body p-2">
                {previewModal.loading ? (
                  <div className="py-5 text-center">
                    <i className="fas fa-spinner fa-spin fa-2x text-muted" />
                    <p className="text-muted mt-2">Loading previews…</p>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    {/* ── Left: Student Worksheet ── */}
                    <div>
                      <div style={{
                        background: '#f1f5f9', borderRadius: '6px 6px 0 0',
                        padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600,
                        color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <i className="fas fa-file-image text-primary" /> Student Worksheet
                      </div>
                      <div style={{
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderTop: 'none', borderRadius: '0 0 6px 6px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                        padding: 8,
                      }}>
                        {previewModal.worksheetUrl ? (
                          <ZoomableImage src={previewModal.worksheetUrl} alt="Student Worksheet" />
                        ) : (
                          <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8' }}>
                            <i className="fas fa-file-image fa-3x mb-2 d-block" />
                            Not available
                          </div>
                        )}
                      </div>
                    </div>

                    {/* ── Right: Handbook / Answer Key ── */}
                    <div>
                      <div style={{
                        background: '#f1f5f9', borderRadius: '6px 6px 0 0',
                        padding: '6px 12px', fontSize: '0.78rem', fontWeight: 600,
                        color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5,
                        display: 'flex', alignItems: 'center', gap: 6,
                      }}>
                        <i className="fas fa-book text-success" /> Handbook / Answer Key
                      </div>
                      <div style={{
                        background: '#f8fafc', border: '1px solid #e2e8f0',
                        borderTop: 'none', borderRadius: '0 0 6px 6px',
                        maxHeight: '80vh',
                        overflow: 'auto',
                        display: 'flex', alignItems: 'flex-start', justifyContent: 'center',
                        padding: 8,
                      }}>
                        {previewModal.handbookUrl ? (
                          previewModal.handbookType === 'pdf'
                            ? <iframe src={previewModal.handbookUrl} title="Handbook" style={{ width: '100%', height: '80vh', border: 'none', borderRadius: 4 }} />
                            : <ZoomableImage src={previewModal.handbookUrl} alt="Handbook / Answer Key" />
                        ) : (
                          <div style={{ margin: 'auto', textAlign: 'center', color: '#94a3b8' }}>
                            <i className="fas fa-book fa-3x mb-2 d-block" />
                            Not available
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="modal-footer py-2">
                <button type="button" className="btn btn-secondary btn-sm" onClick={closePreview}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViewStudentAutoMarking;
